import { useEffect } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'

/**
 * SSEProvider — conexión única de Server-Sent Events al backend.
 *
 * Mantiene abierto `GET /events/stream` mientras el usuario esté autenticado y
 * traduce los eventos del servidor en invalidaciones de React Query, eliminando
 * el polling (refetchInterval) de los hooks.
 *
 * Por qué `@microsoft/fetch-event-source` y no el `EventSource` nativo: el
 * nativo no permite enviar el header `Authorization: Bearer <token>`, que este
 * endpoint exige (auth por JWT de admin).
 *
 * Reconexión: la librería reintenta sola ante caídas de red. Si el backend
 * responde 401/403 (token expirado al reconectar), refrescamos el token; el
 * cambio de `token` re-dispara el efecto y reabre la conexión con el token nuevo.
 */

interface ServerEvent {
  event_type: string
  [key: string]: unknown
}

/** Error que detiene el retry automático para forzar un refresh de token. */
class SSEAuthError extends Error {}

const SSE_RECONNECT_MAX_MS = 30_000

/**
 * Traduce un evento del servidor en acciones de cache.
 * Centraliza el mapeo evento → queryKey para que migrar un hook nuevo a SSE sea
 * agregar un `case` acá y quitar su `refetchInterval`.
 */
function handleServerEvent(event: ServerEvent, qc: QueryClient): void {
  switch (event.event_type) {
    case 'BILLING_DOCUMENT_UPDATED':
      // Refresca toda la rama de facturación (documentos y derivados) y,
      // si el comprobante documenta un Receivable (Fase 7), también su
      // chip de detalle — invalidar todo ['receivables'] es más simple que
      // llevar el receivable_id en el payload del evento y sigue la misma
      // filosofía de "refrescar la rama entera" que ya usa ['billing'].
      qc.invalidateQueries({ queryKey: ['billing'] })
      qc.invalidateQueries({ queryKey: ['receivables'] })
      break

    case 'APPOINTMENT_UPDATED':
      // Se creó/editó/canceló un turno → refresca calendario y tabla en vivo.
      qc.invalidateQueries({ queryKey: ['crm', 'appointments'] })
      break

    case 'VISIT_REPORT_UPDATED':
      // Nuevo reporte de visita → refresca la lista de reportes.
      qc.invalidateQueries({ queryKey: ['crm', 'reports'] })
      break

    case 'ATTENDANCE_REGISTERED':
      // Un check-in/out cambió jornadas, registros, stats y posibles anomalías.
      // No tocamos ['attendance','qr-token']: rota por TTL, no por este evento.
      qc.invalidateQueries({ queryKey: ['attendance', 'jornadas'] })
      qc.invalidateQueries({ queryKey: ['attendance', 'records'] })
      qc.invalidateQueries({ queryKey: ['attendance', 'stats'] })
      qc.invalidateQueries({ queryKey: ['attendance', 'anomalies'] })
      break

    case 'EMPLOYEE_UPDATED':
      // Se vinculó/actualizó un dispositivo de empleado.
      qc.invalidateQueries({ queryKey: ['attendance', 'employees'] })
      qc.invalidateQueries({ queryKey: ['attendance', 'stats'] })
      break

    case 'MIGRATION_JOB_UPDATED': {
      // El job ETL llegó a estado terminal (review | failed). Refresca SOLO ese job.
      const jobId = typeof event.job_id === 'string' ? event.job_id : undefined
      qc.invalidateQueries({
        queryKey: jobId ? ['migration-review', jobId] : ['migration-review'],
      })
      break
    }

    case 'CASH_TRANSACTION_CREATED':
      // Un movimiento/arqueo cambió saldos, historial y analytics de tesorería.
      qc.invalidateQueries({ queryKey: ['treasury'] })
      break

    case 'STOCK_CHANGED': {
      // ADR-053: sub-tipo define qué refrescar
      const tipo = typeof event.tipo === 'string' ? event.tipo : undefined
      if (tipo === 'movement_created' && event.raw_material_id) {
        qc.invalidateQueries({ queryKey: ['stock', 'raw-materials'] })
        qc.invalidateQueries({ queryKey: ['stock', 'raw-materials', String(event.raw_material_id)] })
      } else if (tipo === 'sale_completed' || tipo === 'sale_cancelled') {
        qc.invalidateQueries({ queryKey: ['stock', 'sales'] })
        qc.invalidateQueries({ queryKey: ['stock', 'dashboard'] })
      } else if (tipo === 'low_stock') {
        qc.invalidateQueries({ queryKey: ['stock', 'raw-materials'] })
        qc.invalidateQueries({ queryKey: ['stock', 'dashboard'] })
      } else if (tipo === 'billing_failed') {
        qc.invalidateQueries({ queryKey: ['stock', 'sales'] })
      } else {
        // coalesced o desconocido → invalidar todo stock
        qc.invalidateQueries({ queryKey: ['stock'] })
      }
      break
    }

    case 'MESSAGING_IDENTITY_UPDATED':
    case 'MESSAGING_LINK_CHALLENGE_UPDATED':
      qc.invalidateQueries({ queryKey: ['messaging'] })
      qc.invalidateQueries({ queryKey: ['kafi'] })
      qc.invalidateQueries({ queryKey: ['master', 'kafi-overview'] })
      qc.invalidateQueries({ queryKey: ['master', 'kafi-tenants'] })
      break

    case 'MESSAGE_RECEIVED':
    case 'CONVERSATION_UPDATED':
      qc.invalidateQueries({ queryKey: ['kafi'] })
      qc.invalidateQueries({ queryKey: ['master', 'kafi-overview'] })
      qc.invalidateQueries({ queryKey: ['master', 'kafi-tenants'] })
      break

    default:
      // Evento desconocido (forward-compat): lo ignoramos silenciosamente.
      break
  }
}

export function SSEProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword)

  useEffect(() => {
    // Mientras haya cambio de clave pendiente, el token está restringido por
    // get_current_admin (sólo /auth/me|change-password|logout). /events/stream
    // devolvería 403 y dispararía un loop de refresh+reconexión. No conectamos
    // hasta que el usuario pase ese gate.
    if (!isAuthenticated || !token || mustChangePassword) return

    const ctrl = new AbortController()
    let hasConnected = false
    let reconnectAttempt = 0

    fetchEventSource('/api/events/stream', {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
      // Mantener viva la conexión aunque la pestaña pase a segundo plano.
      openWhenHidden: true,
      async onopen(res) {
        const contentType = res.headers.get('content-type') ?? ''
        if (res.ok && contentType.includes('text/event-stream')) {
          if (hasConnected) {
            // SSE is a notification hint bus. A reconnect always refetches
            // authoritative state so dropped/coalesced events cannot leave a
            // dashboard stale.
            void queryClient.invalidateQueries()
          }
          hasConnected = true
          reconnectAttempt = 0
          return // conectado correctamente
        }
        if (res.status === 401 || res.status === 403) {
          throw new SSEAuthError(`SSE auth failed: ${res.status}`)
        }
        // Otros estados → dejamos que la librería reintente.
        throw new Error(`SSE open failed: ${res.status}`)
      },
      onmessage(msg) {
        if (!msg.data) return
        try {
          handleServerEvent(JSON.parse(msg.data) as ServerEvent, queryClient)
        } catch {
          // Payload no-JSON o malformado: lo ignoramos.
        }
      },
      onerror(err) {
        if (err instanceof SSEAuthError) {
          // Token vencido: refrescamos. El nuevo token cambia la dependencia del
          // efecto, que reabre la conexión. NO relanzamos para que la librería
          // reintente con el token nuevo en lugar de cortar el ciclo.
          void useAuthStore.getState().refreshAuth()
          return 1_000
        }
        // Error de red u otro: la librería reintenta con jitter para evitar
        // thundering-herd reconnects when a backend process restarts.
        reconnectAttempt += 1
        const exponential = Math.min(
          1_000 * 2 ** Math.min(reconnectAttempt, 5),
          SSE_RECONNECT_MAX_MS,
        )
        return Math.round(exponential * (0.75 + Math.random() * 0.5))
      },
      onclose() {
        if (!ctrl.signal.aborted) {
          throw new Error('SSE stream closed; reconnect required')
        }
      },
    }).catch(() => {
      // Abort esperado al desmontar o al cambiar el token; nada que hacer.
    })

    return () => ctrl.abort()
  }, [token, isAuthenticated, mustChangePassword, queryClient])

  return <>{children}</>
}
